import React, { type ReactNode, useState } from 'react';

import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import PageIcon from '@atlaskit/icon/core/page';
import IconTile from '@atlaskit/icon/icon-tile';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';
import type { Knowledge } from '@atlassian/conversation-assistant-agent/ui/KnowledgePillList';

import { ExpandableBody, type Tool } from './ExpandableBody';

const headerLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.CreateAgentCardPlan.headerLabel',
	defaultMessage: 'Agent plan',
	description: 'Small label shown above the agent name in the card header.',
});

const headerLabelDraft = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.CreateAgentCardPlan.headerLabelDraft',
	defaultMessage: 'Agent',
	description: 'Small label shown above the agent name in the card header when in draft state.',
});

const draftLozenge = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.CreateAgentCardPlan.draftLozenge',
	defaultMessage: 'Draft',
	description:
		'Lozenge text inline with the header label, indicating the agent has not been published.',
});

const publishedLozenge = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.CreateAgentCardPlan.publishedLozenge',
	defaultMessage: 'Published',
	description: 'Lozenge text inline with the header label, indicating the agent is published.',
});

const shortAndBriefLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.CreateAgentCardPlan.shortAndBriefLabel',
	defaultMessage: 'Short and brief',
	description:
		'Accessible label for the chevron-up button in the header that collapses the expanded card body.',
});

type AgentCardStatus = 'plan' | 'draft' | 'published';

type CreateAgentCardPlanProps = {
	agentAri?: string;
	agentName: string;
	cloudId?: string;
	description: string;
	instructions: string;
	knowledge: Knowledge[];
	tools: Tool[];
	status?: AgentCardStatus;
	primaryAction?: ReactNode;
	secondaryAction?: ReactNode;
	testId?: string;
	imageUrl?: string;
	agentIdentityAccountId?: string;
	agentNamedId?: string;
};

const styles = cssMap({
	card: {
		width: '100%',
		boxSizing: 'border-box',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
		overflow: 'hidden',
	},
	header: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
	},
	headerTopRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
	},
	headerLabels: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
	},
	headerContent: {
		flex: 1,
		minWidth: '0',
	},
	titleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		minWidth: '0',
	},
	avatarWrapper: {
		flexShrink: 0,
	},
	titleText: {
		minWidth: '0',
		flex: 1,
		overflow: 'hidden',
	},
	titleSpan: {
		display: 'block',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	bodyWrapper: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	footer: {
		width: '100%',
		boxSizing: 'border-box',
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
});

export const CreateAgentCardPlan = ({
	agentAri,
	agentName,
	cloudId,
	description,
	instructions,
	knowledge,
	tools,
	status,
	primaryAction,
	secondaryAction,
	testId,
	imageUrl,
	agentIdentityAccountId,
	agentNamedId,
}: CreateAgentCardPlanProps): React.JSX.Element => {
	const intl = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);
	const cardStatus = status ?? (agentAri ? 'draft' : 'plan');

	const handleExpand = () => {
		setIsExpanded(true);
	};

	return (
		<Box xcss={styles.card} testId={testId}>
			<Box xcss={styles.header}>
				<Flex alignItems="center" justifyContent="space-between" gap="space.100">
					<Stack space="space.075" xcss={styles.headerContent}>
						<Box xcss={styles.headerLabels}>
							<Text weight="bold">
								<FormattedMessage {...(cardStatus === 'plan' ? headerLabel : headerLabelDraft)} />
							</Text>
							{cardStatus === 'draft' && (
								<Lozenge appearance="neutral">
									<FormattedMessage {...draftLozenge} />
								</Lozenge>
							)}
							{cardStatus === 'published' && (
								<Lozenge appearance="success">
									<FormattedMessage {...publishedLozenge} />
								</Lozenge>
							)}
						</Box>
						<Box xcss={styles.titleRow}>
							<Box xcss={styles.avatarWrapper}>
								{!agentAri && !imageUrl ? (
									<IconTile icon={PageIcon} label="" appearance="red" size="medium" />
								) : (
									<AgentAvatar
										agentId={agentAri}
										agentIdentityAccountId={agentIdentityAccountId}
										agentNamedId={agentNamedId}
										imageUrl={imageUrl}
										name={agentName}
										size="medium"
									/>
								)}
							</Box>
							<Box xcss={styles.titleText}>
								<Heading size="small" as="h3">
									<Box as="span" xcss={styles.titleSpan}>
										{agentName}
									</Box>
								</Heading>
							</Box>
						</Box>
					</Stack>
					{isExpanded && (
						<IconButton
							appearance="subtle"
							spacing="compact"
							isTooltipDisabled={false}
							icon={ChevronUpIcon}
							label={intl.formatMessage(shortAndBriefLabel)}
							onClick={() => setIsExpanded(false)}
							testId="rovo-create-agent-card-collapse"
						/>
					)}
				</Flex>
			</Box>

			<Box xcss={styles.bodyWrapper}>
				<ExpandableBody
					cloudId={cloudId}
					description={description}
					instructions={instructions}
					knowledge={knowledge}
					tools={tools}
					isExpanded={isExpanded}
					onExpand={handleExpand}
				/>
			</Box>

			<Box xcss={styles.footer}>
				<Flex justifyContent="end" alignItems="center" gap="space.100">
					{secondaryAction}
					{primaryAction}
				</Flex>
			</Box>
		</Box>
	);
};
